import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, ComposedChart, Legend } from 'recharts';
import { UsersIcon, ChevronDownIcon, CalendarIcon, MapPinIcon } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { TabNavigation } from '../../../components/operations/TabNavigation';
import { OperationsSwitcher } from '../../../components/operations/OperationsSwitcher';
import { SummaryCard } from '../../../components/operations/costs/SummaryCard';
import { InsightCard, InsightItem } from '../../../components/operations/costs/InsightCard';
import { Button } from '../../../components/ui/Button';
const TEAM_TABS = [{
  label: 'Shift Planner',
  path: ''
}, {
  label: 'Leave Timeline',
  path: 'leave'
}, {
  label: 'Team Directory',
  path: 'directory'
}, {
  label: 'Performance',
  path: 'performance'
}, {
  label: 'Costs',
  path: 'costs'
}];
// Mock Data
const HOURLY_RATES_DATA = [{
  role: 'Shift Leader',
  count: 2,
  rate: 550,
  monthly: 176000
}, {
  role: 'Grill Master',
  count: 3,
  rate: 500,
  monthly: 240000
}, {
  role: 'Kitchen Staff',
  count: 4,
  rate: 400,
  monthly: 256000
}, {
  role: 'Delivery Coordinator',
  count: 2,
  rate: 420,
  monthly: 134400
}, {
  role: 'Prep Cook',
  count: 3,
  rate: 380,
  monthly: 182400
}];
const TREND_DATA = [{
  month: 'Jul',
  cost: 62000,
  revenuePct: 9.8
}, {
  month: 'Aug',
  cost: 65000,
  revenuePct: 9.5
}, {
  month: 'Sep',
  cost: 68000,
  revenuePct: 9.2
}, {
  month: 'Oct',
  cost: 72000,
  revenuePct: 10.1
}, {
  month: 'Nov',
  cost: 75000,
  revenuePct: 10.0
}, {
  month: 'Dec',
  cost: 80000,
  revenuePct: 10.3
}];
const LOCATION_DATA = [{
  name: 'Vračar',
  staff: 5,
  cost: 28000,
  revPct: 9.8,
  costPerOrder: 145
}, {
  name: 'Banovo Brdo',
  staff: 4,
  cost: 22000,
  revPct: 10.5,
  costPerOrder: 158
}, {
  name: 'Novi Beograd',
  staff: 3,
  cost: 18000,
  revPct: 10.2,
  costPerOrder: 152
}, {
  name: 'SMASH Vračar',
  staff: 1,
  cost: 6000,
  revPct: 11.2,
  costPerOrder: 168
}, {
  name: 'SMASH NB',
  staff: 1,
  cost: 6000,
  revPct: 12.1,
  costPerOrder: 175
}];
const HOURS_BY_DAY = [{
  day: 'Mon',
  hours: 128,
  intensity: 'medium'
}, {
  day: 'Tue',
  hours: 120,
  intensity: 'medium'
}, {
  day: 'Wed',
  hours: 136,
  intensity: 'medium-high'
}, {
  day: 'Thu',
  hours: 140,
  intensity: 'medium-high'
}, {
  day: 'Fri',
  hours: 160,
  intensity: 'high'
}, {
  day: 'Sat',
  hours: 168,
  intensity: 'peak'
}, {
  day: 'Sun',
  hours: 120,
  intensity: 'medium'
}];
const EFFICIENCY_DATA = [{
  name: 'Marko S.',
  role: 'Grill Master',
  hours: 72,
  orders: 312,
  ordHr: 4.3,
  revenue: 468000,
  revHr: 6500,
  rank: 1
}, {
  name: 'Ana P.',
  role: 'Kitchen Staff',
  hours: 68,
  orders: 285,
  ordHr: 4.2,
  revenue: 399000,
  revHr: 5868,
  rank: 2
}, {
  name: 'Stefan J.',
  role: 'Shift Leader',
  hours: 75,
  orders: 298,
  ordHr: 4.0,
  revenue: 447000,
  revHr: 5960,
  rank: 3
}, {
  name: 'Jovana K.',
  role: 'Kitchen Staff',
  hours: 64,
  orders: 245,
  ordHr: 3.8,
  revenue: 343000,
  revHr: 5359,
  rank: 4
}, {
  name: 'Nikola R.',
  role: 'Prep Cook',
  hours: 60,
  orders: 220,
  ordHr: 3.7,
  revenue: 308000,
  revHr: 5133,
  rank: 5
}, {
  name: 'Milan D.',
  role: 'Grill Master',
  hours: 70,
  orders: 252,
  ordHr: 3.6,
  revenue: 378000,
  revHr: 5400,
  rank: 6
}, {
  name: 'Petar M.',
  role: 'Kitchen Staff',
  hours: 58,
  orders: 185,
  ordHr: 3.2,
  revenue: 259000,
  revHr: 4466,
  rank: 7,
  warning: true
}];
const INSIGHTS: InsightItem[] = [{
  id: '1',
  type: 'insight',
  icon: 'trend-up',
  text: 'Labor cost increased 6.7% vs last month',
  subtext: 'Due to holiday staffing requirements in December'
}, {
  id: '2',
  type: 'insight',
  icon: 'warning',
  text: 'SMASH NB has highest cost per order',
  subtext: '175 RSD vs 153 RSD average across locations'
}, {
  id: '3',
  type: 'insight',
  icon: 'check',
  text: 'Cost per order improved 5% vs last month',
  subtext: 'Despite volume increase, efficiency has improved'
}, {
  id: '4',
  type: 'insight',
  icon: 'chart',
  text: 'Vračar most efficient location',
  subtext: '145 RSD/order, 9.8% of revenue'
}];
const OPPORTUNITIES: InsightItem[] = [{
  id: '1',
  type: 'opportunity',
  priority: 'high',
  text: 'Reduce overtime on Saturdays',
  subtext: '47 OT hours = 23,500 RSD extra cost'
}, {
  id: '2',
  type: 'opportunity',
  priority: 'high',
  text: 'Cross-train Petar M. on grill',
  subtext: 'Below avg efficiency: 3.2 ord/hr'
}, {
  id: '3',
  type: 'opportunity',
  priority: 'medium',
  text: 'Optimize SMASH NB staffing',
  subtext: '12.1% labor ratio is too high for current volume'
}, {
  id: '4',
  type: 'opportunity',
  priority: 'low',
  text: 'Consider part-time role for Tue/Wed',
  subtext: 'Slow periods show excess capacity'
}];
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-background-card border border-white/10 p-3 rounded-lg shadow-xl" data-id="element-2013">
        <p className="text-white font-bold mb-1" data-id="element-2014">{label}</p>
        {payload.map((entry: any, index: number) => <p key={index} style={{
        color: entry.color
      }} className="text-sm" data-id="element-2015">
            {entry.name}: {entry.value.toLocaleString()} {entry.unit || ''}
          </p>)}
      </div>;
  }
  return null;
};
export function Costs() {
  const [timeRange, setTimeRange] = useState('December 2025');
  const [locationFilter, setLocationFilter] = useState('All Locations');
  return <div className="space-y-6 animate-in fade-in duration-500 pb-10" data-id="element-2016">
      {/* Header & Navigation */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2017">
        <h1 className="text-2xl font-bold text-text-primary" data-id="element-2018">Labor Costs</h1>
        <div className="flex items-center gap-3" data-id="element-2019">
          <Button variant="outline" className="flex items-center gap-2" data-id="element-2020">
            <MapPinIcon className="w-4 h-4" data-id="element-2021" />
            {locationFilter}
            <ChevronDownIcon className="w-4 h-4 ml-1 opacity-50" data-id="element-2022" />
          </Button>
          <Button variant="outline" className="flex items-center gap-2" data-id="element-2023">
            <CalendarIcon className="w-4 h-4" data-id="element-2024" />
            {timeRange}
            <ChevronDownIcon className="w-4 h-4 ml-1 opacity-50" data-id="element-2025" />
          </Button>
        </div>
      </div>

      <OperationsSwitcher data-id="element-2026" />
      <TabNavigation tabs={TEAM_TABS} basePath="/operations/team" data-id="element-2027" />

      {/* Section 1: Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2028">
        <SummaryCard title="Total Labor Cost (Dec)" value="80,000 RSD" subtext="10.3% of Revenue" subtextColor="accent" delay={0.1} data-id="element-2029" />
        <SummaryCard title="Cost Per Order" value="153 RSD" trend="5% vs last month" trendDirection="down" subtextColor="success" delay={0.2} data-id="element-2030" />
        <SummaryCard title="Avg Hourly Rate" value="450 RSD" subtext="Across all roles" subtextColor="default" delay={0.3} data-id="element-2031" />
      </div>

      {/* Section 2: Hourly Rates by Role */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.4
    }} data-id="element-2032">
        <Card noPadding className="overflow-hidden" data-id="element-2033">
          <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/5" data-id="element-2034">
            <h3 className="font-bold text-lg text-white" data-id="element-2035">
              Hourly Rates by Role
            </h3>
            <Button variant="outline" size="sm" data-id="element-2036">
              + Add Role
            </Button>
          </div>
          <div className="overflow-x-auto" data-id="element-2037">
            <table className="w-full text-sm text-left" data-id="element-2038">
              <thead className="text-xs text-text-secondary uppercase bg-white/5" data-id="element-2039">
                <tr data-id="element-2040">
                  <th className="px-6 py-4 font-medium" data-id="element-2041">Role</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-2042">Count</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-2043">Avg Rate</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-2044">
                    Monthly Est.
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5" data-id="element-2045">
                {HOURLY_RATES_DATA.map((item, index) => <tr key={index} className="hover:bg-white/5 transition-colors" data-id="element-2046">
                    <td className="px-6 py-4 font-medium text-white" data-id="element-2047">
                      {item.role}
                    </td>
                    <td className="px-6 py-4 text-right text-text-secondary" data-id="element-2048">
                      {item.count}
                    </td>
                    <td className="px-6 py-4 text-right text-text-secondary" data-id="element-2049">
                      {item.rate} RSD
                    </td>
                    <td className="px-6 py-4 text-right text-white font-medium" data-id="element-2050">
                      {item.monthly.toLocaleString()} RSD
                    </td>
                  </tr>)}
                <tr className="bg-white/5 font-bold" data-id="element-2051">
                  <td className="px-6 py-4 text-white" data-id="element-2052">TOTAL</td>
                  <td className="px-6 py-4 text-right text-white" data-id="element-2053">14</td>
                  <td className="px-6 py-4 text-right text-white" data-id="element-2054">
                    450 RSD avg
                  </td>
                  <td className="px-6 py-4 text-right text-accent-primary" data-id="element-2055">
                    988,800 RSD
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </motion.div>

      {/* Section 3 & 4: Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2056">
        {/* Labor Cost Trend */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.5
      }} data-id="element-2057">
          <Card className="h-full flex flex-col" data-id="element-2058">
            <div className="flex justify-between items-center mb-6" data-id="element-2059">
              <h3 className="font-bold text-lg text-white" data-id="element-2060">Labor Cost Trend</h3>
              <div className="flex items-center gap-4 text-xs" data-id="element-2061">
                <div className="flex items-center gap-2" data-id="element-2062">
                  <span className="w-3 h-3 rounded-full bg-accent-primary" data-id="element-2063"></span>
                  <span className="text-text-secondary" data-id="element-2064">Cost</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2065">
                  <span className="w-3 h-3 rounded-full border-2 border-accent-success" data-id="element-2066"></span>
                  <span className="text-text-secondary" data-id="element-2067">% Rev</span>
                </div>
                <Button variant="ghost" size="sm" className="h-6 text-xs" data-id="element-2068">
                  6M ▼
                </Button>
              </div>
            </div>
            <div className="h-[300px] w-full" data-id="element-2069">
              <ResponsiveContainer width="100%" height="100%" data-id="element-2070">
                <ComposedChart data={TREND_DATA} data-id="element-2071">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} data-id="element-2072" />
                  <XAxis dataKey="month" stroke="#9CA3AF" tick={{
                  fill: '#9CA3AF'
                }} axisLine={false} tickLine={false} data-id="element-2073" />
                  <YAxis yAxisId="left" stroke="#9CA3AF" tick={{
                  fill: '#9CA3AF'
                }} axisLine={false} tickLine={false} tickFormatter={value => `${value / 1000}K`} data-id="element-2074" />
                  <YAxis yAxisId="right" orientation="right" stroke="#9CA3AF" tick={{
                  fill: '#9CA3AF'
                }} axisLine={false} tickLine={false} tickFormatter={value => `${value}%`} data-id="element-2075" />
                  <Tooltip content={<CustomTooltip data-id="element-2077" />} data-id="element-2076" />
                  <Line yAxisId="left" type="monotone" dataKey="cost" stroke="#8B5CF6" strokeWidth={3} dot={{
                  r: 4,
                  fill: '#8B5CF6',
                  strokeWidth: 0
                }} activeDot={{
                  r: 6
                }} name="Labor Cost" unit=" RSD" data-id="element-2078" />
                  <Line yAxisId="right" type="monotone" dataKey="revenuePct" stroke="#22C55E" strokeWidth={2} dot={{
                  r: 4,
                  fill: '#0F1419',
                  stroke: '#22C55E',
                  strokeWidth: 2
                }} name="% of Revenue" unit="%" data-id="element-2079" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>

        {/* Labor Cost by Location */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.6
      }} data-id="element-2080">
          <Card className="h-full flex flex-col" data-id="element-2081">
            <h3 className="font-bold text-lg text-white mb-6" data-id="element-2082">
              Labor Cost by Location
            </h3>
            <div className="overflow-x-auto" data-id="element-2083">
              <table className="w-full text-sm" data-id="element-2084">
                <thead data-id="element-2085">
                  <tr className="text-text-secondary border-b border-white/10" data-id="element-2086">
                    <th className="text-left pb-3 font-medium" data-id="element-2087">LOCATION</th>
                    <th className="text-right pb-3 font-medium" data-id="element-2088">STAFF</th>
                    <th className="text-right pb-3 font-medium" data-id="element-2089">COST</th>
                    <th className="text-right pb-3 font-medium" data-id="element-2090">% REV</th>
                    <th className="text-right pb-3 font-medium" data-id="element-2091">$/ORD</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5" data-id="element-2092">
                  {LOCATION_DATA.map((loc, i) => <tr key={i} data-id="element-2093">
                      <td className="py-3 font-medium text-white" data-id="element-2094">
                        {loc.name}
                      </td>
                      <td className="py-3 text-right text-text-secondary" data-id="element-2095">
                        {loc.staff}
                      </td>
                      <td className="py-3 text-right text-white" data-id="element-2096">
                        {loc.cost.toLocaleString()}
                      </td>
                      <td className="py-3 text-right text-text-secondary" data-id="element-2097">
                        {loc.revPct}%
                      </td>
                      <td className="py-3 text-right font-medium" style={{
                    color: loc.costPerOrder > 160 ? '#EF4444' : loc.costPerOrder < 150 ? '#22C55E' : '#FFFFFF'
                  }} data-id="element-2098">
                        {loc.costPerOrder}
                      </td>
                    </tr>)}
                  <tr className="font-bold bg-white/5" data-id="element-2099">
                    <td className="py-3 pl-2 text-white" data-id="element-2100">TOTAL</td>
                    <td className="py-3 text-right text-white" data-id="element-2101">14</td>
                    <td className="py-3 text-right text-accent-primary" data-id="element-2102">
                      80,000
                    </td>
                    <td className="py-3 text-right text-white" data-id="element-2103">10.3%</td>
                    <td className="py-3 text-right text-white" data-id="element-2104">153</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mini Bar Chart Visualization */}
            <div className="mt-6 h-[150px] w-full" data-id="element-2105">
              <ResponsiveContainer width="100%" height="100%" data-id="element-2106">
                <BarChart data={LOCATION_DATA} layout="vertical" margin={{
                top: 0,
                right: 30,
                left: 0,
                bottom: 0
              }} data-id="element-2107">
                  <XAxis type="number" hide data-id="element-2108" />
                  <YAxis dataKey="name" type="category" width={100} tick={{
                  fill: '#9CA3AF',
                  fontSize: 10
                }} axisLine={false} tickLine={false} data-id="element-2109" />
                  <Tooltip cursor={{
                  fill: 'rgba(255,255,255,0.05)'
                }} content={<CustomTooltip data-id="element-2111" />} data-id="element-2110" />
                  <Bar dataKey="cost" name="Labor Cost" radius={[0, 4, 4, 0]} barSize={12} data-id="element-2112">
                    {LOCATION_DATA.map((entry, index) => <Cell key={`cell-${index}`} fill={index > 2 ? '#EF4444' : '#8B5CF6'} fillOpacity={0.8} data-id="element-2113" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Section 5: Hours Worked Analysis */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.7
    }} data-id="element-2114">
        <Card className="p-6" data-id="element-2115">
          <div className="flex justify-between items-center mb-6" data-id="element-2116">
            <h3 className="font-bold text-lg text-white" data-id="element-2117">
              Hours Worked Analysis
            </h3>
            <Button variant="ghost" size="sm" data-id="element-2118">
              This Week ▼
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-2119">
            <div className="bg-white/5 rounded-lg p-4 border border-white/5" data-id="element-2120">
              <div className="text-text-secondary text-xs uppercase font-bold mb-1" data-id="element-2121">
                Total Hours
              </div>
              <div className="text-2xl font-bold text-white" data-id="element-2122">892</div>
              <div className="text-xs text-text-secondary" data-id="element-2123">this month</div>
            </div>
            <div className="bg-white/5 rounded-lg p-4 border border-white/5" data-id="element-2124">
              <div className="text-text-secondary text-xs uppercase font-bold mb-1" data-id="element-2125">
                Overtime
              </div>
              <div className="text-2xl font-bold text-yellow-500" data-id="element-2126">47</div>
              <div className="text-xs text-text-secondary" data-id="element-2127">5.3% of total</div>
            </div>
            <div className="bg-white/5 rounded-lg p-4 border border-white/5" data-id="element-2128">
              <div className="text-text-secondary text-xs uppercase font-bold mb-1" data-id="element-2129">
                Avg Hours/Staff
              </div>
              <div className="text-2xl font-bold text-white" data-id="element-2130">63.7</div>
              <div className="text-xs text-text-secondary" data-id="element-2131">hrs/person</div>
            </div>
            <div className="bg-white/5 rounded-lg p-4 border border-white/5" data-id="element-2132">
              <div className="text-text-secondary text-xs uppercase font-bold mb-1" data-id="element-2133">
                Utilization
              </div>
              <div className="text-2xl font-bold text-accent-success" data-id="element-2134">85%</div>
              <div className="text-xs text-text-secondary" data-id="element-2135">vs 80% target</div>
            </div>
          </div>

          <div className="h-[200px] w-full" data-id="element-2136">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2137">
              <BarChart data={HOURS_BY_DAY} layout="vertical" margin={{
              top: 0,
              right: 30,
              left: 20,
              bottom: 0
            }} data-id="element-2138">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255,255,255,0.1)" data-id="element-2139" />
                <XAxis type="number" stroke="#9CA3AF" tick={{
                fill: '#9CA3AF'
              }} axisLine={false} tickLine={false} data-id="element-2140" />
                <YAxis dataKey="day" type="category" stroke="#9CA3AF" tick={{
                fill: '#9CA3AF'
              }} axisLine={false} tickLine={false} width={40} data-id="element-2141" />
                <Tooltip cursor={{
                fill: 'rgba(255,255,255,0.05)'
              }} content={<CustomTooltip data-id="element-2143" />} data-id="element-2142" />
                <Bar dataKey="hours" name="Hours" radius={[0, 4, 4, 0]} barSize={20} data-id="element-2144">
                  {HOURS_BY_DAY.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.intensity === 'peak' ? '#8B5CF6' : entry.intensity === 'high' ? '#7C3AED' : '#6D28D9'} fillOpacity={entry.intensity === 'peak' ? 1 : 0.7} data-id="element-2145" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </motion.div>

      {/* Section 6: Staff Efficiency Metrics */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.8
    }} data-id="element-2146">
        <Card noPadding className="overflow-hidden" data-id="element-2147">
          <div className="p-6 border-b border-white/5" data-id="element-2148">
            <h3 className="font-bold text-lg text-white" data-id="element-2149">Staff Efficiency</h3>
          </div>
          <div className="overflow-x-auto" data-id="element-2150">
            <table className="w-full text-sm text-left" data-id="element-2151">
              <thead className="text-xs text-text-secondary uppercase bg-white/5" data-id="element-2152">
                <tr data-id="element-2153">
                  <th className="px-6 py-4 font-medium" data-id="element-2154">Employee</th>
                  <th className="px-6 py-4 font-medium" data-id="element-2155">Role</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-2156">Hours</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-2157">Orders</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-2158">Ord/Hr</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-2159">Revenue</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-2160">Rev/Hr</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5" data-id="element-2161">
                {EFFICIENCY_DATA.map((staff, index) => <tr key={index} className="hover:bg-white/5 transition-colors group" data-id="element-2162">
                    <td className="px-6 py-4 font-medium text-white flex items-center gap-2" data-id="element-2163">
                      {staff.rank === 1 && <span className="text-lg" data-id="element-2164">🥇</span>}
                      {staff.rank === 2 && <span className="text-lg" data-id="element-2165">🥈</span>}
                      {staff.rank === 3 && <span className="text-lg" data-id="element-2166">🥉</span>}
                      {staff.warning && <span className="text-lg" data-id="element-2167">⚠️</span>}
                      {staff.name}
                    </td>
                    <td className="px-6 py-4 text-text-secondary" data-id="element-2168">
                      {staff.role}
                    </td>
                    <td className="px-6 py-4 text-right text-text-secondary" data-id="element-2169">
                      {staff.hours}
                    </td>
                    <td className="px-6 py-4 text-right text-text-secondary" data-id="element-2170">
                      {staff.orders}
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-white" data-id="element-2171">
                      {staff.ordHr}
                    </td>
                    <td className="px-6 py-4 text-right text-text-secondary" data-id="element-2172">
                      {staff.revenue.toLocaleString()}K
                    </td>
                    <td className={`px-6 py-4 text-right font-medium ${staff.warning ? 'text-red-500' : 'text-accent-success'}`} data-id="element-2173">
                      {staff.revHr.toLocaleString()} RSD
                    </td>
                  </tr>)}
                <tr className="bg-white/5 font-bold border-t border-white/10" data-id="element-2174">
                  <td className="px-6 py-4 text-white" colSpan={2} data-id="element-2175">
                    Team Average
                  </td>
                  <td className="px-6 py-4 text-right text-white" data-id="element-2176">66.7</td>
                  <td className="px-6 py-4 text-right text-white" data-id="element-2177">256.7</td>
                  <td className="px-6 py-4 text-right text-white" data-id="element-2178">3.8</td>
                  <td className="px-6 py-4 text-right text-white" data-id="element-2179">371.7K</td>
                  <td className="px-6 py-4 text-right text-white" data-id="element-2180">5,526 RSD</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </motion.div>

      {/* Section 7: Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2181">
        <InsightCard title="Labor Insights" items={INSIGHTS} variant="insights" delay={0.9} data-id="element-2182" />
        <InsightCard title="Optimization Opportunities" items={OPPORTUNITIES} variant="opportunities" delay={1.0} data-id="element-2183" />
      </div>
    </div>;
}